import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { usePermissions } from "@/hooks/usePermissions";
import { PERMISSION_KEYS } from "@/permissions/permissionKeys";
import { getPageDeniedContent } from "@/permissions/permissionMessages";
import PermissionDenied from "@/Utility/PermissionDenied/PermissionDenied";
import {
  fetchProjectDashboard,
  fetchWorkItemsByProjectId,
} from "@/Redux/Thunks/projectManagementThunk";
import { fetchBacklogData } from "@/Redux/Thunks/backlogThunk";
import { fetchProjectSettings } from "@/Redux/Thunks/projectSettingsThunk";
import {
  clearProjectDashboard,
  seedWorkspaceProjectTitle,
  selectWorkspaceProjectName,
  selectWorkspaceProjectId,
} from "@/Redux/Slices/projectDashboardSlice";
import {
  selectProjectsData,
  selectProjectsAllListData,
} from "@/Redux/Slices/projectsManagementSlice";
import Typography from "@/Utility/Typography";
import ProjectNavigation from "./project-detail/ProjectNavigation";
import { PROJECT_TAB_NAV_ITEMS } from "./project-detail/projectNavigation.mapper";
import ProjectDashboard from "./project-detail/dashboard/ProjectDashboard";
import ProjectsIssues from "./project-detail/issues/ProjectsIssues";
import ProjectBoards from "./project-detail/boards/ProjectsBoards";
import ProjectSettings from "./project-detail/settings/ProjectSettings";
import WorkItemDetailModal from "@/Pages/Issues/WorkItemDetailModal";
import {
  ISSUES_LISTING_FILTER_DEFAULT,
  buildProjectIssuesListBody,
} from "./project-detail/issues/issues.mapper";
import {
  WORK_ITEMS_LIST_PAGE_SIZE_STORAGE_KEY,
  readStoredListPageSize,
} from "@/Utility/Pagination/listPaginationConstants";
import { buildWorkItemDetailPath } from "@/utils/workItemDetailUrl";
import {
  navigateWorkItemDetailModal,
  getWorkItemDetailModalStateAfterSave,
} from "@/utils/workItemDetailModalNavigation";
import Backlog from "./project-detail/backlog/Backlog";
import BackArrow from "@/Utility/BackArrow/BackArrow";
import Button from "@/Utility/Button/Button";
import { RiRefreshLine } from "react-icons/ri";
import { ROUTES } from "../../constants";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const VALID_PROJECT_TABS = new Set(["dashboard", "issues", "boards", "backlog", "settings"]);
const WORK_ITEM_PROJECT_TAB_IDS = new Set(["issues", "boards", "backlog"]);

/** Synced by {@link ProjectsBoards} so work-item modal save can refetch Kanban with the same `assigneeId`. */
const defaultBoardsListRefetchContext = () => ({
  mode: "table",
  kanbanAssigneeIds: [],
});

const Projects = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectSlug } = useParams();
  const { can } = usePermissions();
  const canViewProjectDashboard = can(PERMISSION_KEYS.PROJECTS_DASHBOARD_VIEW);
  const canManageProjectSettings = can(PERMISSION_KEYS.PROJECTS_SETTINGS_MANAGE);
  const canViewProjectIssuesTab = can(PERMISSION_KEYS.WORK_ITEMS_VIEW);
  const canViewProjectBoardsTab = can(PERMISSION_KEYS.KANBAN_VIEW);
  const canViewProjectBacklogTab = can(PERMISSION_KEYS.WORK_ITEMS_VIEW);
  const canViewAnyProjectWorkItemTab =
    canViewProjectIssuesTab || canViewProjectBoardsTab || canViewProjectBacklogTab;

  const projectNavItems = useMemo(
    () =>
      PROJECT_TAB_NAV_ITEMS.filter((item) => {
        if (item.id === "dashboard" && !canViewProjectDashboard) return false;
        if (item.id === "settings" && !canManageProjectSettings) return false;
        if (item.id === "issues" && !canViewProjectIssuesTab) return false;
        if (item.id === "boards" && !canViewProjectBoardsTab) return false;
        if (item.id === "backlog" && !canViewProjectBacklogTab) return false;
        return true;
      }),
    [
      canViewProjectDashboard,
      canManageProjectSettings,
      canViewProjectIssuesTab,
      canViewProjectBoardsTab,
      canViewProjectBacklogTab,
    ]
  );

  const defaultWorkspaceTab = useMemo(() => {
    if (canViewProjectDashboard) return "dashboard";
    if (canViewProjectIssuesTab) return "issues";
    if (canViewProjectBoardsTab) return "boards";
    if (canViewProjectBacklogTab) return "backlog";
    if (canManageProjectSettings) return "settings";
    return "dashboard";
  }, [
    canViewProjectDashboard,
    canViewProjectIssuesTab,
    canViewProjectBoardsTab,
    canViewProjectBacklogTab,
    canManageProjectSettings,
  ]);
  const workspaceTitle = useSelector(selectWorkspaceProjectName);
  const workspaceProjectId = useSelector(selectWorkspaceProjectId);
  const projectsData = useSelector(selectProjectsData);
  const allProjectsData = useSelector(selectProjectsAllListData);

  const nameFromProjectsLists = useMemo(() => {
    if (!projectSlug) return null;
    const slug = String(projectSlug).trim();
    const slugLower = slug.toLowerCase();
    const matchIn = (list) => {
      if (!Array.isArray(list) || !list.length) return null;
      const byId = list.find((p) => String(p?.id ?? p?.projectId ?? "") === slug);
      if (byId) return byId.projectName ?? byId.name ?? null;
      const byKey = list.find(
        (p) =>
          String(p?.projectKey ?? p?.key ?? "")
            .trim()
            .toLowerCase() === slugLower
      );
      return byKey ? (byKey.projectName ?? byKey.name ?? null) : null;
    };
    const fromAll = matchIn(allProjectsData?.list);
    if (fromAll) return String(fromAll).trim();
    const fromPage = matchIn(projectsData?.list);
    return fromPage ? String(fromPage).trim() : null;
  }, [projectSlug, allProjectsData?.list, projectsData?.list]);

  useEffect(() => {
    if (!projectSlug || !nameFromProjectsLists) return;
    if (workspaceProjectId === projectSlug && workspaceTitle) return;
    dispatch(seedWorkspaceProjectTitle({ projectId: projectSlug, name: nameFromProjectsLists }));
  }, [dispatch, projectSlug, nameFromProjectsLists, workspaceProjectId, workspaceTitle]);

  const projectName = useMemo(() => {
    const fromStore = workspaceTitle?.trim();
    if (fromStore) return fromStore;
    if (!projectSlug) return "";
    if (UUID_RE.test(projectSlug)) return "\u2026";
    return String(projectSlug)
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }, [workspaceTitle, projectSlug]);

  useLayoutEffect(() => {
    if (!projectSlug) return;
    dispatch(clearProjectDashboard());
    if (canViewProjectDashboard) {
      dispatch(fetchProjectDashboard(projectSlug));
    }
  }, [dispatch, projectSlug, canViewProjectDashboard]);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const rawTab = useMemo(() => {
    const fromUrl = searchParams.get("tab");
    if (fromUrl != null && String(fromUrl).trim() !== "") {
      const t = String(fromUrl).trim();
      return VALID_PROJECT_TABS.has(t) ? t : defaultWorkspaceTab;
    }
    return defaultWorkspaceTab;
  }, [searchParams, defaultWorkspaceTab]);

  /** Tab shown under the modal (or normal URL tab). Legacy `?tab=issue-detail` maps to `returnTab` or issues. */
  const visualTab = useMemo(() => {
    if (rawTab === "issue-detail") {
      const rtRaw = searchParams.get("returnTab");
      let rt = "issues";
      if (rtRaw) {
        try {
          rt = decodeURIComponent(rtRaw);
        } catch {
          rt = rtRaw;
        }
      }
      return VALID_PROJECT_TABS.has(rt) ? rt : "issues";
    }
    return rawTab;
  }, [rawTab, searchParams]);

  const [workItemModal, setWorkItemModal] = useState(null);
  const [issueModalReturnTab, setIssueModalReturnTab] = useState("issues");
  const boardsListRefetchContextRef = useRef(defaultBoardsListRefetchContext());

  /** Legacy `?tab=issue-detail&id=` → shareable work item URL under `/issues/work-items/`. */
  useEffect(() => {
    if (!projectSlug || rawTab !== "issue-detail") return;
    const id = searchParams.get("id")?.trim();
    if (!id) {
      navigate(`/projects/${encodeURIComponent(projectSlug)}?tab=${visualTab}`, {
        replace: true,
      });
      return;
    }
    navigate(
      buildWorkItemDetailPath({
        projectSlug,
        workItemId: id,
      }),
      { replace: true }
    );
  }, [projectSlug, rawTab, visualTab, searchParams, navigate]);

  useEffect(() => {
    if (rawTab !== "reports" || !projectSlug) return;
    const tab = canViewProjectDashboard
      ? "dashboard"
      : canViewProjectIssuesTab
        ? "issues"
        : canViewProjectBoardsTab
          ? "boards"
          : canViewProjectBacklogTab
            ? "backlog"
            : canManageProjectSettings
              ? "settings"
              : "dashboard";
    navigate(`/projects/${projectSlug}?tab=${tab}`, { replace: true });
  }, [
    rawTab,
    projectSlug,
    navigate,
    canViewProjectDashboard,
    canViewProjectIssuesTab,
    canViewProjectBoardsTab,
    canViewProjectBacklogTab,
    canManageProjectSettings,
  ]);

  /** Legacy default was dashboard; pick first allowed tab when dashboard is not available. */
  useEffect(() => {
    if (!projectSlug) return;
    if (canViewProjectDashboard) return;
    if (rawTab !== "dashboard") return;
    if (new URLSearchParams(location.search).has("tab")) return;
    const next = canViewProjectIssuesTab
      ? "issues"
      : canViewProjectBoardsTab
        ? "boards"
        : canViewProjectBacklogTab
          ? "backlog"
          : canManageProjectSettings
            ? "settings"
            : null;
    if (next) navigate(`/projects/${projectSlug}?tab=${next}`, { replace: true });
  }, [
    projectSlug,
    rawTab,
    canViewProjectDashboard,
    canViewProjectIssuesTab,
    canViewProjectBoardsTab,
    canViewProjectBacklogTab,
    canManageProjectSettings,
    location.search,
    navigate,
  ]);

  /** Redirect away from a project work-item tab the user cannot access. */
  useEffect(() => {
    if (!projectSlug) return;
    const allowed =
      (visualTab === "issues" && canViewProjectIssuesTab) ||
      (visualTab === "boards" && canViewProjectBoardsTab) ||
      (visualTab === "backlog" && canViewProjectBacklogTab);
    if (allowed || !WORK_ITEM_PROJECT_TAB_IDS.has(visualTab)) return;
    const next = projectNavItems[0]?.id;
    if (next && next !== visualTab) {
      navigate(`/projects/${projectSlug}?tab=${next}`, { replace: true });
      return;
    }
    if (!next) navigate(ROUTES.PROJECTS, { replace: true });
  }, [
    projectSlug,
    visualTab,
    canViewProjectIssuesTab,
    canViewProjectBoardsTab,
    canViewProjectBacklogTab,
    projectNavItems,
    navigate,
  ]);

  /** Persist Work Items tab filters while the work-item modal is open (same project). */
  const [workItemsListingFilter, setWorkItemsListingFilter] = useState(() => ({
    ...ISSUES_LISTING_FILTER_DEFAULT,
  }));
  const [workItemsCurrentPage, setWorkItemsCurrentPage] = useState(1);

  useEffect(() => {
    setWorkItemsListingFilter({ ...ISSUES_LISTING_FILTER_DEFAULT });
    setWorkItemsCurrentPage(1);
    boardsListRefetchContextRef.current = defaultBoardsListRefetchContext();
  }, [projectSlug]);

  const openWorkItemDetail = useCallback(
    (id, _projectIdFromRow) => {
      if (!canViewAnyProjectWorkItemTab || !id || !projectSlug) return;
      setIssueModalReturnTab(visualTab);
      setWorkItemModal({ issueId: String(id), epicParentId: null });
    },
    [projectSlug, visualTab, canViewAnyProjectWorkItemTab]
  );

  const closeIssueDetailModal = useCallback(() => {
    setWorkItemModal(null);
  }, []);

  const refetchWorkItemHostTab = useCallback(
    async (returnTab) => {
      if (!projectSlug) return;
      if (returnTab === "issues") {
        await dispatch(
          fetchWorkItemsByProjectId({
            projectId: projectSlug,
            body: buildProjectIssuesListBody(workItemsListingFilter, {
              page: workItemsCurrentPage,
              limit: readStoredListPageSize(WORK_ITEMS_LIST_PAGE_SIZE_STORAGE_KEY),
            }),
          })
        ).unwrap();
      } else if (returnTab === "backlog") {
        await dispatch(fetchBacklogData(projectSlug)).unwrap();
      } else if (returnTab === "boards") {
        const ctx = boardsListRefetchContextRef.current ?? defaultBoardsListRefetchContext();
        if (ctx.mode === "kanban") {
          const body = { page: 1, limit: 100 };
          if (ctx.kanbanAssigneeIds?.length) {
            body.assigneeId = ctx.kanbanAssigneeIds;
          }
          await dispatch(fetchWorkItemsByProjectId({ projectId: projectSlug, body })).unwrap();
        } else {
          await dispatch(fetchWorkItemsByProjectId({ projectId: projectSlug })).unwrap();
        }
      } else if (returnTab === "dashboard" && canViewProjectDashboard) {
        await dispatch(fetchProjectDashboard(projectSlug)).unwrap();
      } else if (returnTab === "settings" && canManageProjectSettings) {
        await dispatch(fetchProjectSettings(projectSlug)).unwrap();
      } else {
        await dispatch(fetchWorkItemsByProjectId({ projectId: projectSlug })).unwrap();
      }
    },
    [
      dispatch,
      projectSlug,
      workItemsListingFilter,
      workItemsCurrentPage,
      canViewProjectDashboard,
      canManageProjectSettings,
    ]
  );

  /** Refetch list behind modal; return to epic after saving a child, else close. */
  const handleWorkItemDetailSaved = useCallback(async () => {
    if (!projectSlug) return;
    const nextModal = getWorkItemDetailModalStateAfterSave(workItemModal);
    try {
      await refetchWorkItemHostTab(issueModalReturnTab);
    } catch {
      /* still navigate or close */
    }
    if (nextModal) {
      setWorkItemModal(nextModal);
    } else {
      closeIssueDetailModal();
    }
  }, [
    projectSlug,
    workItemModal,
    issueModalReturnTab,
    refetchWorkItemHostTab,
    closeIssueDetailModal,
  ]);

  const projectNavActiveTab = workItemModal?.issueId ? issueModalReturnTab : visualTab;

  const navigationActiveTab = useMemo(() => {
    const base = projectNavActiveTab;
    if (projectNavItems.some((i) => i.id === base)) return base;
    return projectNavItems[0]?.id ?? defaultWorkspaceTab;
  }, [projectNavActiveTab, projectNavItems, defaultWorkspaceTab]);

  const workItemDetailOpener = canViewAnyProjectWorkItemTab ? openWorkItemDetail : undefined;

  const legacyShareWorkItemId = searchParams.get("workItemId")?.trim();
  if (legacyShareWorkItemId && projectSlug) {
    return (
      <Navigate
        to={buildWorkItemDetailPath({
          projectSlug,
          workItemId: legacyShareWorkItemId,
        })}
        replace
      />
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-2">
          <BackArrow className="mt-[-6px]" />
          <Typography variant="h3">{projectName}</Typography>
        </div>
        {visualTab === "dashboard" && canViewProjectDashboard ? (
          <Button
            variant="neutral"
            size="vsm"
            title="Sync"
            className="btn-reset square-btn"
            onClick={() => {
              if (projectSlug) {
                dispatch(clearProjectDashboard());
                dispatch(fetchProjectDashboard(projectSlug));
              }
            }}
            aria-label="Refresh project data"
          >
            <RiRefreshLine className="text-lg text-text-secondary" />
          </Button>
        ) : null}
      </div>
      <ProjectNavigation
        activeTab={navigationActiveTab}
        items={projectNavItems}
        onTabChange={(tab) => {
          setWorkItemModal(null);
          const params = new URLSearchParams();
          params.set("tab", tab);
          navigate(`/projects/${encodeURIComponent(projectSlug)}?${params.toString()}`);
        }}
      />

      {projectNavItems.length === 0 ? (
        <PermissionDenied
          title="Project workspace"
          description="Your role does not include access to this project’s dashboard, work items, or settings."
          to={ROUTES.PROJECTS}
          buttonLabel="Go to Projects"
        />
      ) : (
        <>
          {visualTab === "dashboard" &&
            (canViewProjectDashboard ? (
              <ProjectDashboard projectSlug={projectSlug} />
            ) : (
              <PermissionDenied
                {...getPageDeniedContent(PERMISSION_KEYS.PROJECTS_DASHBOARD_VIEW)}
              />
            ))}

          {visualTab === "issues" &&
            (canViewProjectIssuesTab ? (
              <ProjectsIssues
                projectSlug={projectSlug}
                listingFilter={workItemsListingFilter}
                setListingFilter={setWorkItemsListingFilter}
                currentPage={workItemsCurrentPage}
                setCurrentPage={setWorkItemsCurrentPage}
                onOpenWorkItemDetail={workItemDetailOpener}
              />
            ) : (
              <PermissionDenied {...getPageDeniedContent(PERMISSION_KEYS.WORK_ITEMS_VIEW)} />
            ))}

          {visualTab === "boards" &&
            (canViewProjectBoardsTab ? (
              <ProjectBoards
                onOpenWorkItemDetail={workItemDetailOpener}
                boardsListRefetchContextRef={boardsListRefetchContextRef}
              />
            ) : (
              <PermissionDenied {...getPageDeniedContent(PERMISSION_KEYS.KANBAN_VIEW)} />
            ))}

          {visualTab === "backlog" &&
            (canViewProjectBacklogTab ? (
              <Backlog onOpenWorkItemDetail={workItemDetailOpener} />
            ) : (
              <PermissionDenied {...getPageDeniedContent(PERMISSION_KEYS.WORK_ITEMS_VIEW)} />
            ))}

          {visualTab === "settings" &&
            (canManageProjectSettings ? (
              <ProjectSettings projectSlug={projectSlug} />
            ) : (
              <PermissionDenied
                {...getPageDeniedContent(PERMISSION_KEYS.PROJECTS_SETTINGS_MANAGE)}
              />
            ))}

          {workItemModal?.issueId && canViewAnyProjectWorkItemTab ? (
            <WorkItemDetailModal
              projectSlug={projectSlug}
              issueId={workItemModal.issueId}
              linkReturnTab={issueModalReturnTab}
              onClose={closeIssueDetailModal}
              onSaved={handleWorkItemDetailSaved}
              onOpenRelatedWorkItem={(relatedId, options) => {
                if (!canViewAnyProjectWorkItemTab || !relatedId) return;
                setWorkItemModal((prev) => navigateWorkItemDetailModal(prev, relatedId, options));
              }}
              onProjectIdChange={(newProjectId) => {
                if (!newProjectId || String(newProjectId) === String(projectSlug)) return;
                closeIssueDetailModal();
              }}
            />
          ) : null}
        </>
      )}
    </div>
  );
};

export default Projects;
